import React, { useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components/macro";
import { rgba } from "polished";
import { FormattedMessage } from "react-intl";

import Icon from "../../components/Icon";
import { BRAND, VISIBLE_SUGGESTIONS } from "./brand";

const Wrapper = styled.section`
  width: 100%;
  max-width: 1140px;
  margin: 0 auto;
  padding: 0.5rem 1rem 2rem;
`;

const Heading = styled.h2`
  font-family: ${props => props.theme.fontFamilyBrand};
  font-weight: 500;
  font-size: 1.25rem;
  letter-spacing: 0.03em;
  text-align: center;
  margin: 0.5rem 0 1.25rem;
  color: ${props => props.headingColor};

  @media (${props => props.theme.tabletScreen}) {
    font-size: 1.6rem;
    margin-bottom: 1.75rem;
  }
`;

const Grid = styled.ol`
  list-style: none;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin: 0;
  padding: 0;

  @media (${props => props.theme.tabletScreen}) {
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
  }

  @media (${props => props.theme.desktopScreen}) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

/* Space the label occupies at the foot of the card. Logo artwork is held above
 * it; photographs run under it. */
const LABEL = "2.75rem";

/* The label overlays the artwork on a white plate that is solid under the text
 * at bottom-left and eases to 80% toward top-right, so the photograph stays
 * visible through the lighter corner. */
const Name = styled.strong`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  min-height: ${LABEL};
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background-image: linear-gradient(
    to top right,
    ${props => rgba(props.theme.white, 1)} 0%,
    ${props => rgba(props.theme.white, 0.8)} 100%
  );
  font-family: ${props => props.theme.fontFamilyBrand};
  font-weight: 600;
  font-size: ${props => props.theme.fontSizes.small};
  line-height: 1.25;
  letter-spacing: 0.02em;
  text-align: left;
  color: inherit;
  hyphens: auto;

  @media (${props => props.theme.tabletScreen}) {
    font-size: ${props => props.theme.fontSizes.default};
  }
`;

/* Deterministic per-topic variation, so a grid of artwork-less cards does not
 * look like one tile repeated. */
const shade = name => (name.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0) % 5) * 0.02;

/* Fallback for topics with no artwork yet: the topic's initial drawn as a
 * watermark in the brand color. */
const Initial = styled.span`
  position: absolute;
  inset: 0 0 ${LABEL};
  z-index: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${props => props.theme.fontFamilyBrand};
  font-weight: 700;
  font-size: 3.5rem;
  line-height: 1;
  color: ${props => rgba(props.accentColor, 0.14 + props.variant)};
  transition: transform 0.45s ease-out;

  @media (${props => props.theme.tabletScreen}) {
    font-size: 4.5rem;
  }
`;

const Tile = styled.button`
  position: relative;
  display: block;
  width: 100%;
  /* Matches the 4:3 topic photography, so a picture fills the card uncropped.
   * min-height is the floor for browsers without aspect-ratio (IE 11 is still
   * in browserslist): the card's only content is an absolutely positioned
   * label, so without it there would be nothing to give the card height. It
   * sits below the ratio's computed height at every breakpoint, so it never
   * fights the ratio. */
  min-height: 120px;
  aspect-ratio: 4 / 3;
  border: 0;
  appearance: none;
  cursor: pointer;
  overflow: hidden;
  border-radius: ${props => props.theme.borderRadiusMedium};
  background-color: ${props => props.theme.white};
  color: ${props => props.accentColor};
  box-shadow: ${props => props.theme.boxShadowSmall};
  transition: box-shadow 0.25s, transform 0.25s;

  background-image: ${props => (props.image ? `url("${props.image}")` : "none")};
  background-position: center;
  background-repeat: no-repeat;

  /* A photo covers the whole card and runs under the label. A logo mark is a
   * brand asset: contain it inside the padding box, which reserves the label's
   * height, so the plate never sits on top of the mark. */
  ${({ photo }) =>
    photo
      ? `
        padding: 0;
        background-size: cover;
      `
      : `
        padding: 0.5rem 0.5rem ${LABEL};
        background-origin: content-box;
        background-size: contain;
      `}

  &:hover,
  &:focus-visible {
    outline: none;
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.boxShadowLifted};

    ${Initial} {
      transform: scale(1.06);
    }
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px ${props => rgba(props.accentColor, 0.55)};
  }

  &:active {
    transform: translateY(0);
  }
`;

const ToggleRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1.25rem;
`;

const Toggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.25rem;
  border: 1px solid ${props => rgba(props.headingColor, 0.45)};
  border-radius: 2rem;
  background-color: ${props => rgba(props.headingColor, 0.12)};
  color: ${props => props.headingColor};
  font-family: ${props => props.theme.fontFamilyBase};
  font-size: ${props => props.theme.fontSizes.small};
  letter-spacing: 0.03em;
  cursor: pointer;
  appearance: none;
  transition: background-color 0.25s, border-color 0.25s;

  &:hover,
  &:focus-visible {
    outline: none;
    background-color: ${props => rgba(props.headingColor, 0.22)};
    border-color: ${props => props.headingColor};
  }

  i {
    line-height: 0;
  }
`;

const SuggestionGrid = ({ suggestions, onSelect, visibleCount, accentColor, headingColor }) => {
  const [expanded, setExpanded] = useState(false);

  if (suggestions.length === 0) {
    return null;
  }

  const truncatable = suggestions.length > visibleCount;
  const visible = expanded || !truncatable ? suggestions : suggestions.slice(0, visibleCount);

  return (
    <Wrapper>
      <Heading headingColor={headingColor}>
        <FormattedMessage id="pages.PampersSearch.suggestionsTitle" />
      </Heading>
      <Grid>
        {visible.map(suggestion => (
          <li key={suggestion.name}>
            <Tile
              type="button"
              accentColor={accentColor}
              image={suggestion.image}
              photo={suggestion.photo}
              onClick={() => onSelect(suggestion.name)}
            >
              {!suggestion.image && (
                <Initial
                  accentColor={accentColor}
                  variant={shade(suggestion.name)}
                  aria-hidden="true"
                >
                  {suggestion.name.charAt(0)}
                </Initial>
              )}
              <Name>{suggestion.name}</Name>
            </Tile>
          </li>
        ))}
      </Grid>
      {truncatable && (
        <ToggleRow>
          <Toggle
            type="button"
            headingColor={headingColor}
            aria-expanded={expanded}
            onClick={() => setExpanded(e => !e)}
          >
            <FormattedMessage
              id={expanded ? "pages.PampersSearch.showLess" : "pages.PampersSearch.showMore"}
              values={{ count: suggestions.length - visibleCount }}
            />
            <Icon name="chevron-up" size="small" direction={expanded ? "up" : "down"} />
          </Toggle>
        </ToggleRow>
      )}
    </Wrapper>
  );
};

SuggestionGrid.propTypes = {
  suggestions: PropTypes.array,
  onSelect: PropTypes.func.isRequired,
  visibleCount: PropTypes.number,
  /* The brand primary color, used here as the card text and watermark color. */
  accentColor: PropTypes.string,
  /* Color for the heading and expand toggle, which sit on the page background. */
  headingColor: PropTypes.string,
};

SuggestionGrid.defaultProps = {
  suggestions: [],
  visibleCount: VISIBLE_SUGGESTIONS,
  accentColor: BRAND.primary,
  headingColor: BRAND.primary,
};

export default SuggestionGrid;
